import { useCallback, useEffect, useRef, useState } from "react";
import { Select, Spin } from "antd";
import { connect, mapReadPretty } from "@formily/react";
import { PreviewText } from "@formily/antd-v5";
import { useTranslation } from "react-i18next";
import { ApiService } from "../api";

type Option = { value: string; label: string };

const SEARCH_DEBOUNCE_MS = 300;

/**
 * A field that picks from a maintained list — an asset, a vendor, a cost centre.
 *
 * The gap it closes: every dropdown of that kind was previously typed into each
 * form's schema as a fixed `enum`. The same list existed in five templates,
 * went stale in four of them, and nothing could say who owns an asset or
 * whether a vendor's contract had expired. The lists now live in one table
 * (`catalog`); this is what lets a form read one.
 *
 * **The stored value is the entity's `code`, not its name** — the opposite
 * choice from `UserSelect`, and deliberately. A name gets renamed into Kazakh
 * or by a reorganisation and every stored answer silently stops resolving; a
 * code is chosen once and kept. The cost is that a report has to ask the
 * catalog to turn a code back into a name, which is what
 * `form_schema.entity_option_labels` is for.
 *
 * Three details are copied from `UserSelect` rather than reinvented, because
 * each of them is a bug somebody already had:
 *
 * * an out-of-order response guard, so a slow search for "p" cannot land after
 *   a fast one for "pump" and replace the right answers with stale ones;
 * * a debounce, so typing does not issue a request per keystroke;
 * * the **merged stored value**, so a saved answer whose entity is not in the
 *   current page of results still renders as itself instead of an empty box.
 */
const EntitySearchSelect = ({
  value,
  onChange,
  placeholder,
  disabled,
  style,
  entityType,
}: any) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<Option[]>([]);
  const [fetching, setFetching] = useState(false);
  const requestSeq = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback(
    (term: string) => {
      if (!entityType) {
        setOptions([]);
        return;
      }
      const seq = ++requestSeq.current;
      setFetching(true);
      ApiService.apiCatalogEntityOptions(entityType, term || undefined)
        .then((rows) => {
          if (seq !== requestSeq.current) return;
          setOptions(
            (rows ?? []).map((r: any) => ({ value: r.code, label: r.label }))
          );
        })
        .catch(() => {
          // A list that cannot be reached leaves the field empty rather than
          // breaking the form around it: the rest of the case is still fillable
          // and the answer can be given later.
          if (seq === requestSeq.current) setOptions([]);
        })
        .finally(() => {
          if (seq === requestSeq.current) setFetching(false);
        });
    },
    [entityType]
  );

  const handleSearch = useCallback(
    (term: string) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => search(term), SEARCH_DEBOUNCE_MS);
    },
    [search]
  );

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // The stored code is shown as itself until the list answers. Without this a
  // filled-in field renders blank for as long as the request takes, and blank
  // on a case whose list has since been retired — which is exactly the case
  // somebody is most likely to be looking at.
  const merged =
    value && !options.some((o) => o.value === value)
      ? [{ value, label: value }, ...options]
      : options;

  return (
    <Select
      showSearch
      allowClear
      value={value || undefined}
      disabled={disabled}
      style={{ width: "100%", ...(style ?? {}) }}
      placeholder={
        placeholder ?? (entityType ? t("Start typing…") : t("No list chosen"))
      }
      // The API already filters; filtering again client-side would hide results
      // whose label differs from the code that matched.
      filterOption={false}
      onSearch={handleSearch}
      onFocus={() => {
        if (options.length === 0) search("");
      }}
      onChange={(next) => onChange?.(next ?? "")}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      options={merged}
    />
  );
};

/**
 * `mapReadPretty` gives it the same read-only rendering as a plain Input, so a
 * closed permit shows the answer as text rather than a live dropdown somebody
 * might think they can still change.
 */
const EntitySelect = connect(
  EntitySearchSelect,
  mapReadPretty(PreviewText.Input)
);

export default EntitySelect;
